import {Box, Heading, Image, Stack, Text, VStack} from 'platform/foundation';

import {isNil} from 'ramda';

import {Nullish, suffixTestId, TestIdProps} from 'shared/web';

import {EquipmentDocument} from '../../../api/digitalCertificateApi';
import equipmentDocumentIllustration from '../../../assets/images/equipment-document.png';
import {ButtonLink} from '../../../components/ButtonLink/ButtonLink';
import {i18n} from '../../../i18n/i18n';

interface EquipmentDocumentCardProps extends TestIdProps {
  equipmentDocument: EquipmentDocument | Nullish;
}

/**
 * The manufacturer's equipment list from the VIN check, attached to the inspection as a PDF and
 * offered for download. Rendered only when the certificate carries an attachment; the link is the
 * signed storage URL from the latest certificate response.
 */
export function EquipmentDocumentCard(props: EquipmentDocumentCardProps) {
  if (isNil(props.equipmentDocument)) {
    return null;
  }

  return (
    <Box
      backgroundColor="fill.greyLight"
      borderRadius="medium"
      padding={[4, 6, 6, 6]}
      data-testid={suffixTestId('equipmentDocument', props)}
    >
      <Stack
        direction={['column', 'row', 'row', 'row']}
        spacing={[4, 10, 10, 10]}
        align={['flex-start', 'center', 'center', 'center']}
      >
        <Image
          src={equipmentDocumentIllustration}
          alt=""
          width={ILLUSTRATION_SIZE}
          height={ILLUSTRATION_SIZE}
        />
        <VStack spacing={4} align="flex-start">
          <VStack spacing={3}>
            <Heading size={3} headingLevel={CARD_HEADING_LEVEL}>
              {i18n.t('equipmentDocument.title')}
            </Heading>
            <Text size="base" color="secondary">
              {i18n.t('equipmentDocument.description')}
            </Text>
          </VStack>
          <ButtonLink
            variant="inverted"
            href={props.equipmentDocument.downloadUrl}
            target="_blank"
            rel="noreferrer"
            leftIcon="files_default/file_type_PDF"
            title={i18n.t('downloadEquipmentPDF')}
            data-testid={suffixTestId('equipmentDocumentDownload', props)}
          />
        </VStack>
      </Stack>
    </Box>
  );
}

// 148 px in the design, expressed in spacing steps.
const ILLUSTRATION_SIZE = 37;
// The card sits under the level-1 heading of the Features section.
const CARD_HEADING_LEVEL = 2;
